// v5.13.7: install the pre-auth unhandledrejection swallow BEFORE anything
// else touches the runtime. This file is a synchronous side-effect import:
// the listener is registered at module-evaluation time so it beats every
// React component's lifecycle.
import './boot/silence-pre-auth-rejections.js';

import React from 'react';
import ReactDOM from 'react-dom/client';
import App, { ErrorBoundary } from './App';
import { APP_VERSION } from './config/brand';
import { migrateRetiredModelChoices } from './services/model-selector';
import './styles/index.css';

// Published as a global so ErrorBoundary can stamp the version on a crash
// report WITHOUT importing a config module. That boundary is the one component
// that has to render when the app is broken, so it deliberately imports no
// stylesheet, icon or config — it reads this defensively and simply omits the
// line if it is missing.
declare global {
  interface Window { __QM_APP_VERSION__?: string }
}

if (typeof window !== 'undefined') window.__QM_APP_VERSION__ = APP_VERSION;

// SPEC 2026-10-08 D15: move this device's saved model choices (task pins,
// saved gears, Assistant quick picks, the global model) off the models that
// left the pick lists, once, before App reads any of them. Never blocks boot.
try { migrateRetiredModelChoices(); } catch { /* storage unavailable */ }

// ── Measured viewport height (--qm-vvh), mobile wave 4 (2026-08-14) ─────────
// 100dvh promises to track the browser chrome but real Android browsers keep
// breaking that promise (operator screenshots: Brave's bottom toolbar left a
// dead white band exactly its own height). visualViewport.height is the
// MEASURED truth, so the shell sizes from it wherever it exists; the CSS
// falls back var(--qm-vvh, var(--qm-vh)) → dvh → vh. The scale guard keeps
// pinch-zoom from shrinking the layout: the visual viewport narrows when
// zoomed in, and resizing the app to the zoomed window would be wrong.
if (typeof window !== 'undefined' && window.visualViewport) {
  const vv = window.visualViewport;
  let raf = 0;
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  const apply = () => {
    raf = 0;
    if (Math.abs(vv.scale - 1) > 0.02) return; // pinch-zoomed: leave layout alone
    document.documentElement.style.setProperty('--qm-vvh', `${Math.round(vv.height)}px`);
  };
  const schedule = () => { if (!raf) raf = window.requestAnimationFrame(apply); };
  // Trailing settle (operator 2026-08-14: after pinch-zoom in/out the bar
  // stuck mid-screen). A pinch gesture emits a stream of resize/scroll events
  // whose LAST value may be transient — and the final return-to-scale-1 state
  // does not always emit its own event. Never trust a single event: re-measure
  // shortly after the stream goes quiet (and after every touch lift), when the
  // scale guard passes and vv.height is the settled truth.
  const settle = () => {
    if (settleTimer) clearTimeout(settleTimer);
    // Cancel any queued frame before the direct call: apply() zeroes the
    // shared raf handle, and leaving a frame pending let a later
    // schedule() queue a second concurrent rAF (audit 2026-08-14 —
    // redundant work only, never a wrong value).
    settleTimer = setTimeout(() => {
      if (raf) { window.cancelAnimationFrame(raf); raf = 0; }
      apply();
    }, 260);
  };
  vv.addEventListener('resize', () => { schedule(); settle(); });
  vv.addEventListener('scroll', settle); // pinch-pans emit vv scrolls, not resizes
  window.addEventListener('touchend', settle, { passive: true });
  window.addEventListener('orientationchange', () => { schedule(); settle(); });
  apply();
}

// Non-null assertion, not a guard: index.html always ships the #root div, and a
// runtime guard here would be a behaviour change rather than an annotation.
const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
